

import { useSelector, useDispatch } from "react-redux";
import { Badge, Box, HStack, Text, IconButton, Icon, Portal, keyframes } from "@chakra-ui/react";
import { FaChevronDown } from "react-icons/fa";
import { IoCall } from "react-icons/io5";
import { motion, AnimatePresence } from "framer-motion";

import WebRTCApp from "./WebRTCApp";
import {
  toggleDialerModal,
  saveUserDialerSettings,
} from "../../redux/webrtc/webrtcSlice";
import { PhoneIcon } from "@chakra-ui/icons";
import useUserSession from "hooks/useUserSession";
import { useFetchItemsQuery } from "api/apiSlice";
import { useEffect } from "react";
import { useModalColors } from "hooks/useModalColors";

const MotionBox = motion(Box);

/**
 * The SIP dialer, docked bottom-right.
 *
 * ★ A ROUND BUTTON, NOT A BAR. The dialer used to be a 380px strip glued to
 * the bottom edge of every screen, sitting over whatever the page put there
 * (the WhatsApp composer, table footers). Closed, it is now one round phone
 * button with a presence dot; open, the phone panel floats above the button
 * as a rounded card and scrolls inside itself. The button stays put in both
 * states and is the one thing that opens and closes the panel.
 */
const FAB_SIZE = 56;
const EDGE = 24;

/**
 * ★ ABOVE THE APP, BENEATH A DRAWER.
 * Chakra stacks its own layers at banner 1200, overlay 1300, modal 1400. The
 * dialer sits just under the overlay so it floats over every page — navbar,
 * sidebar, sticky toolbars — and still passes *under* a drawer or modal
 * instead of covering it. It was 9999/10000, which parked the phone button on
 * top of the WhatsApp composer's send button whenever the lead drawer was
 * open, and left no way to reach either.
 */
const Z_DIALER = 1200;

/** A slow ring breathing out from the button while the line is registered. */
const breathe = keyframes`
  0%   { transform: scale(1);   opacity: 0.55; }
  70%  { transform: scale(1.6); opacity: 0; }
  100% { transform: scale(1.6); opacity: 0; }
`;

const WebRTCModal = () => {
  const colors = useModalColors();

  const dispatch = useDispatch();

  const webrtc = useSelector((state) => state.webrtc);
  const sipStatus = webrtc.sipStatus;
  const isOpen = webrtc.isModalOpen;
  const userSettings = webrtc?.userSettings;

  const isWssEnabled = Boolean(
    userSettings?.status?.wss || userSettings?.modes?.wss?.cid,
  );

  const { user, agencyName } = useUserSession();

  const { data } = useFetchItemsQuery(
    {
      path: `/sipSetting/user/${user?._id}`,
    },
    {
      skip: !user?._id,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: true,
    },
  );

  // Sync API → Redux
  useEffect(() => {
    if (data?.doc) {
      dispatch(saveUserDialerSettings(data.doc));
    }
  }, [data?.doc, dispatch]);

  // If WSS not enabled, UI shouldn't render
  if (!isWssEnabled) return null;
  // Withdrawn while the lead WhatsApp drawer is open: the drawer is a full-
  // height surface and the floating bubble has no business over it. The
  // user's own open/closed state is untouched and comes back with the bubble.
  if (webrtc.dialerHidden) return null;

  const handleToggle = () => dispatch(toggleDialerModal());
  const online = sipStatus === "registered";
  const title = agencyName ? `${agencyName} Dialer` : "Weam Dialer";

  // ★ PORTALLED SO ITS LAYER MEANS WHAT IT SAYS.
  // A fixed element's z-index only counts inside the nearest stacking context
  // of its ancestors, so any wrapper that gains a transform, a filter or a
  // z-index of its own quietly re-parents the dialer and its layer stops
  // being comparable with anything else. Mounting on <body> makes Z_DIALER a
  // single global statement: over the app, under Chakra's overlays.
  return (
    <Portal>
      {/* The phone panel, floating above the button. */}
      <AnimatePresence>
        {isOpen && (
          <MotionBox
            key="dialer-panel"
            position="fixed"
            bottom={`${EDGE + FAB_SIZE + 12}px`}
            right={`${EDGE}px`}
            w={{ base: "calc(100vw - 32px)", sm: "380px" }}
            maxH="calc(100vh - 140px)"
            display="flex"
            flexDirection="column"
            bg={colors.bgDeep}
            color={colors.bodyText}
            borderRadius="2xl"
            boxShadow={colors.modalShadow}
            overflow="hidden"
            border="1px solid"
            borderColor={colors.borderColor}
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 380, damping: 32 }}
            zIndex={Z_DIALER}
            role="dialog"
            aria-label={title}
          >
            {/* Panel header */}
            <Box
              bg={colors.bg}
              color={colors.headingText}
              px={4}
              py={3}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              borderBottom="1px solid"
              borderColor={colors.borderColor}
              flexShrink={0}
            >
              <HStack spacing={2} align="center" minW="0">
                <PhoneIcon w={4} h={4} color={colors.accentGold} />
                <Text fontWeight="bold" fontSize="sm" noOfLines={1}>
                  {title}
                </Text>
                <Badge
                  bg={online ? "success" : "danger"}
                  color="text.onAccent"
                  fontSize="0.65rem"
                  px={2}
                  py={0.5}
                  rounded="full"
                  textTransform="none"
                >
                  {online ? "Online" : "Offline"}
                </Badge>
              </HStack>

              <IconButton
                aria-label="Hide dialer"
                icon={<Icon as={FaChevronDown} />}
                size="sm"
                variant="ghost"
                borderRadius="full"
                color={colors.accentGold}
                onClick={handleToggle}
              />
            </Box>

            {/* Content scrolls inside the card; the page underneath never moves. */}
            <Box bg={colors.bgDeep} flex="1" minH="0" overflowY="auto">
              <WebRTCApp />
            </Box>
          </MotionBox>
        )}
      </AnimatePresence>

      {/* The round phone button — always there, opens and closes the panel. */}
      <Box
        position="fixed"
        bottom={`${EDGE}px`}
        right={`${EDGE}px`}
        w={`${FAB_SIZE}px`}
        h={`${FAB_SIZE}px`}
        zIndex={Z_DIALER + 1}
      >
        {online && !isOpen && (
          <Box
            aria-hidden="true"
            position="absolute"
            inset="0"
            borderRadius="full"
            bg="green.400"
            sx={{
              animation: `${breathe} 2.4s ease-out infinite`,
              '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0 },
            }}
          />
        )}
        <MotionBox
          as="button"
          type="button"
          aria-label={isOpen ? "Hide dialer" : `Open ${title}`}
          aria-expanded={isOpen}
          position="relative"
          w="100%"
          h="100%"
          borderRadius="full"
          bg="green.400"
          color="text.onAccent"
          display="flex"
          alignItems="center"
          justifyContent="center"
          border="1px solid"
          borderColor="whiteAlpha.300"
          boxShadow="0 10px 28px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.25)"
          cursor="pointer"
          onClick={handleToggle}
          initial="rest"
          whileHover="hover"
          whileTap="tap"
          animate="rest"
          variants={{
            rest: { scale: 1, y: 0 },
            hover: { scale: 1.08, y: -2 },
            tap: { scale: 0.94, y: 0 },
          }}
          transition={{ type: "spring", stiffness: 420, damping: 22 }}
          _hover={{
            bgGradient: "linear(to-br, brand.200, brand.400 55%, brand.600)",
            boxShadow:
              "0 14px 34px rgba(0,0,0,0.4), 0 0 0 6px var(--chakra-colors-accent-subtle), inset 0 1px 0 rgba(255,255,255,0.3)",
          }}
          _focus={{ outline: "none" }}
          _focusVisible={{ boxShadow: "0 0 0 4px var(--chakra-colors-accent-subtle)" }}
        >
          {/* The icon answers the hover on its own: the handset tilts as if lifted. */}
          <MotionBox
            display="flex"
            variants={{
              rest: { rotate: 0, scale: 1 },
              hover: { rotate: isOpen ? 0 : -14, scale: 1.12 },
              tap: { rotate: 0, scale: 0.96 },
            }}
            transition={{ type: "spring", stiffness: 500, damping: 18 }}
          >
            {isOpen ? (
              <Icon as={FaChevronDown} boxSize="20px" />
            ) : (
              <Icon as={IoCall} boxSize="26px" filter="drop-shadow(0 1px 1px rgba(0,0,0,0.25))" />
            )}
          </MotionBox>
          {/* Presence, the way a messenger shows it: a dot on the avatar's shoulder. */}
          <Box
            position="absolute"
            right="1px"
            bottom="1px"
            w="15px"
            h="15px"
            borderRadius="full"
            bg={online ? "success" : "danger"}
            border="2.5px solid"
            borderColor="bg.app"
            aria-hidden="true"
          />
        </MotionBox>
      </Box>
    </Portal>
  );
};

export default WebRTCModal;
